import React, { useEffect, useState, useCallback } from 'react';
import Layout from './Layout';
import './css/HomePage.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const REFRESH_MS = 5 * 60 * 1000;
const TZ = 'Asia/Kuala_Lumpur';

// ---------- helpers ----------
const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const todayMYT = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(s) {
  if (!s) return '';
  const [, m, d] = s.split('-').map(Number);
  return `${MONTH_ABBR[m - 1]} ${d}`;
}

function computeDelta(today, yesterday) {
  if (yesterday === 0) {
    if (today === 0) return { dir: 'flat', text: '0%' };
    return { dir: 'up', text: `+${today}` };
  }
  const change = Math.round(((today - yesterday) / yesterday) * 100);
  if (change > 0) return { dir: 'up', text: `+${change}%` };
  if (change < 0) return { dir: 'down', text: `${change}%` };
  return { dir: 'flat', text: '0%' };
}

function buildPoints(data, w, h, padding) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  return data.map((v, i) => {
    const x = padding + (data.length === 1 ? 0 : (i / (data.length - 1)) * (w - 2 * padding));
    const y = h - padding - ((v - min) / range) * (h - 2 * padding);
    return [x, y];
  });
}

// ---------- charts ----------
function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const W = 200, H = 34;
  const pts = buildPoints(data, W, H, 3);
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg className="kpi-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={line} fill="none" stroke={color} strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="2.6" fill={color} />
    </svg>
  );
}

function AreaChart({ data, color, fill }) {
  if (!data || data.length < 2) return null;
  const W = 600, H = 150;
  const pts = buildPoints(data, W, H, 8);
  const lineStr = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaD =
    `M ${pts[0][0].toFixed(1)},${H} ` +
    `L ${pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L ')} ` +
    `L ${pts[pts.length - 1][0].toFixed(1)},${H} Z`;
  const last = pts[pts.length - 1];
  return (
    <svg className="chart-area" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={areaD} fill={fill} />
      <polyline points={lineStr} fill="none" stroke={color} strokeWidth="2.5"
        strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="3.5" fill="#fff"
        stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ---------- live clock ----------
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const day = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'long' }).format(now);
  const time = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(now);
  return (
    <div className="stat-clock">
      <div className="clock-cell"><div className="clock-label">Date</div><div className="clock-value">{date}</div></div>
      <div className="clock-cell"><div className="clock-label">Day</div><div className="clock-value">{day}</div></div>
      <div className="clock-cell time accent"><div className="clock-label">Time (MYT)</div><div className="clock-value">{time}</div></div>
    </div>
  );
}

// ---------- KPI card ----------
function KpiCard({ label, value, accent, inlinePct, inlineColor, delta, yesterdayValue, series, color }) {
  return (
    <div className={`kpi${accent ? ` kpi-accent kpi-accent-${accent}` : ''}`}>
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        {/* <span className={`delta ${delta.dir}`}>
          {delta.dir === 'up' && '▲ '}
          {delta.dir === 'down' && '▼ '}
          {delta.text}
        </span> */}
      </div>
      <div className="kpi-val">
        {typeof value === 'number' ? value.toLocaleString() : value}
        {inlinePct != null && (
          <span className="pct-inline" style={{ color: inlineColor }}> {inlinePct}%</span>
        )}
      </div>
      {/* <div className="kpi-sub">vs {yesterdayValue.toLocaleString()} yesterday</div> */}
      {/* <Sparkline data={series} color={color} /> */}
    </div>
  );
}

// ---------- User stats table ----------
function UserStatsTable({ userStats, monthLabel }) {
  if (!userStats || userStats.length === 0) return null;
  return (
    <div className="user-stats-section">
      <div className="section-eyebrow">Staff activity &mdash; {monthLabel}</div>
      <div className="user-stats-grid">
        {userStats.map((u, i) => (
          <div key={u.name} className="user-card">
            <div className="user-rank">#{i + 1}</div>
            <div className="user-name">{u.name}</div>
            <div className="user-metrics">
              <div className="user-metric">
                <span className="user-metric-val handled-val">{u.handledCount}</span>
                <span className="user-metric-label">Handled</span>
              </div>
              <div className="user-metric-divider" />
              <div className="user-metric">
                <span className="user-metric-val confirmed-val">{u.confirmedCount}</span>
                <span className="user-metric-label">Confirmed</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- export modal ----------
function ExportModal({ from, to, onFromChange, onToChange, onCancel, onExport }) {
  return (
    <div className="modal-overlay">
      <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="exp-title">
        <h3 className="modal-title" id="exp-title">Export Report</h3>
        <p className="modal-sub">Select a date range. Defaults to today.</p>
        <div className="modal-field">
          <label htmlFor="exp-from">Date From</label>
          <input id="exp-from" type="date" value={from} max={to} onChange={(e) => onFromChange(e.target.value)} />
        </div>
        <div className="modal-field">
          <label htmlFor="exp-to">Date To</label>
          <input id="exp-to" type="date" value={to} min={from} onChange={(e) => onToChange(e.target.value)} />
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" onClick={onExport} disabled={!from || !to}>Export</button>
        </div>
      </div>
    </div>
  );
}

// ---------- main ----------
const HomePage = () => {
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [showExport, setShowExport] = useState(false);
  const [dateFrom, setDateFrom] = useState(todayMYT);
  const [dateTo, setDateTo] = useState(todayMYT);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try { setUser(JSON.parse(userData)); } catch (e) { setUser(null); }
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      setRefreshing(true);
      const res = await fetch(`${API_BASE}/api/homes/statistics`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Could not load statistics');
      setData(json);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchStats]);

  if (!user) return null;

  const isAdmin = Number(user.type) === 1;

  const handleExport = () => {
    if (!dateFrom || !dateTo) return;
    const url = `${API_BASE}/api/homes/report?from=${encodeURIComponent(dateFrom)}` +
      `&to=${encodeURIComponent(dateTo)}&uid=${encodeURIComponent(user.pkkey)}`;
    window.open(url, '_blank', 'noopener');
    setShowExport(false);
  };

  const updatedText = lastUpdated
    ? new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(lastUpdated)
    : '—';

  return (
    <Layout>
      <div className="dash-wrap">
        <LiveClock />

        <div className="stat-toolbar">
          <div className="toolbar-left">
            <span className="stat-toolbar-title"><span className="wa-dot" /> WhatsApp Chat Statistics</span>
            {isAdmin && (
              <button className="export-btn" onClick={() => setShowExport(true)}>
                ↧ Export Report
              </button>
            )}
          </div>
          <div className="refresh-group">
            <span className="updated-text">Updated {updatedText}</span>
            <button className="refresh-btn" onClick={fetchStats} disabled={refreshing}>
              <span className={refreshing ? 'spin' : ''}>↻</span> Refresh
            </button>
          </div>
        </div>

        {loading && !data && (
          <div className="dash-state">
            <div className="spinner" />
            <p>Loading statistics…</p>
          </div>
        )}

        {error && !data && (
          <div className="dash-state">
            <div className="error-box">
              <p className="error-title">Couldn't load statistics</p>
              <p className="error-msg">{error}</p>
              <button className="retry-btn" onClick={fetchStats}>Try again</button>
            </div>
          </div>
        )}

        {data && (() => {
          const inSeries      = data.daily.map((d) => d.customersIn);
          const confirmSeries = data.daily.map((d) => d.confirmed);
          const handleSeries  = data.daily.map((d) => d.handled);
          const waitSeries    = data.daily.map((d) => d.waiting);
          const peak = Math.max(...inSeries);
          const avg  = Math.round(inSeries.reduce((a, c) => a + c, 0) / inSeries.length);
          const t = data.today;
          const y = data.yesterday;
          const firstDate = data.daily[0]?.date;
          const lastDate  = data.daily[data.daily.length - 1]?.date;

          return (
            <div className="panel">
              {/* KPI row */}
              <div className="kpi-grid">
                <KpiCard
                  label="Customers In"
                  value={t.customersIn}
                  delta={computeDelta(t.customersIn, y.customersIn)}
                  yesterdayValue={y.customersIn}
                  series={inSeries}
                  color="#64748b"
                />
                <KpiCard
                  label="Confirmed Appt."
                  accent="blue"
                  value={t.confirmed}
                  inlinePct={pct(t.confirmed, t.customersIn)}
                  inlineColor="#2563eb"
                  delta={computeDelta(t.confirmed, y.confirmed)}
                  yesterdayValue={y.confirmed}
                  series={confirmSeries}
                  color="#2563eb"
                />
                <KpiCard
                  label="Handled"
                  accent="green"
                  value={t.handled}
                  inlinePct={pct(t.handled, t.customersIn)}
                  inlineColor="#16a34a"
                  delta={computeDelta(t.handled, y.handled)}
                  yesterdayValue={y.handled}
                  series={handleSeries}
                  color="#16a34a"
                />
                <KpiCard
                  label="Waiting for Appt / Handle"
                  accent="amber"
                  value={t.waiting}
                  inlinePct={pct(t.waiting, t.customersIn)}
                  inlineColor="#d97706"
                  delta={computeDelta(t.waiting, y.waiting)}
                  yesterdayValue={y.waiting}
                  series={waitSeries}
                  color="#d97706"
                />
              </div>

              {/* 14-day chart — hidden
              <div className="chart-head">
                <span className="chart-title">Customers In — last 14 days</span>
                <span className="chart-note">peak {peak} · avg {avg}</span>
              </div>
              <AreaChart data={inSeries} color="#2563eb" fill="rgba(37,99,235,.10)" />
              <div className="chart-axis">
                <span>{shortDate(firstDate)}</span>
                <span>{shortDate(lastDate)}</span>
              </div>
              */}

              {/* User stats */}
              <UserStatsTable userStats={data.userStats} monthLabel={data.monthLabel} />

              {/* Period summary */}
              <div className="period-foot">
                <div className="pcol">
                  <div className="pcol-title">This Month · {data.month.label}</div>
                  <div className="prow"><span>Customers In</span><span className="v">{data.month.customersIn.toLocaleString()}</span></div>
                  <div className="prow"><span>Confirmed</span><span className="v">{data.month.confirmed.toLocaleString()} <small className="b">{pct(data.month.confirmed, data.month.customersIn)}%</small></span></div>
                  <div className="prow"><span>Handled</span><span className="v">{data.month.handled.toLocaleString()} <small className="g">{pct(data.month.handled, data.month.customersIn)}%</small></span></div>
                  <div className="prow"><span>Waiting</span><span className="v">{data.month.waiting.toLocaleString()} <small className="a">{pct(data.month.waiting, data.month.customersIn)}%</small></span></div>
                </div>
                <div className="pcol">
                  <div className="pcol-title">All Time · since launch</div>
                  <div className="prow"><span>Customers In</span><span className="v">{data.allTime.customersIn.toLocaleString()}</span></div>
                  <div className="prow"><span>Confirmed</span><span className="v">{data.allTime.confirmed.toLocaleString()} <small className="b">{pct(data.allTime.confirmed, data.allTime.customersIn)}%</small></span></div>
                  <div className="prow"><span>Handled</span><span className="v">{data.allTime.handled.toLocaleString()} <small className="g">{pct(data.allTime.handled, data.allTime.customersIn)}%</small></span></div>
                  <div className="prow"><span>Waiting</span><span className="v">{data.allTime.waiting.toLocaleString()} <small className="a">{pct(data.allTime.waiting, data.allTime.customersIn)}%</small></span></div>
                </div>
              </div>
            </div>
          );
        })()}

        {isAdmin && showExport && (
          <ExportModal
            from={dateFrom}
            to={dateTo}
            onFromChange={setDateFrom}
            onToChange={setDateTo}
            onCancel={() => setShowExport(false)}
            onExport={handleExport}
          />
        )}
      </div>
    </Layout>
  );
};

export default HomePage;